import { AlertButton, useIonAlert } from '@ionic/react';
import UpdateOverlay from 'pages/Profile/components/UpdateOverlay';
import { useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import { useAuthService } from 'services/Authentication';
import { useFSM } from 'services/DongleController/DongleController';
import { ZephyrResponse } from 'types';
import {
  FirmwareUpdateAvailable,
  FirmwareUpdateResponse,
} from 'types/communication';

export interface CheckUpdateProps {
  updateCB?: (_: boolean) => void;
  failSilent?: boolean;
  force?: boolean;
}

export const useFirmwareUpdate = () => {
  const { t } = useTranslation();
  const [updateStatus, setUpdateStatus] = useState<
    ZephyrResponse | Error | undefined
  >(undefined);
  const [presentAlert] = useIonAlert();
  const fsm = useFSM();
  const auth = useAuthService();

  const checkForUpdates = useMemo(
    () => async (opts: CheckUpdateProps) => {
      const token = auth.userToken.get();
      if (!token) return;

      const { updateCB, failSilent = false, force = false } = opts;
      let message = t('UPDATE_SEARCH_COMPLETE');
      let result: FirmwareUpdateResponse;
      let buttons: AlertButton[] = [{ text: 'Ok' }];
      try {
        result = await fsm.checkForFirmwareUpdate(token, force);
        const updateAvailable = result.type === 'updateavailable';
        updateCB?.(updateAvailable);
        if (updateAvailable) {
          message = `${t('FREAK_UPDATE_AVAILABLE')}: ${
            (result as FirmwareUpdateAvailable).fileName
          }`;
          buttons = [
            { text: 'Cancel', role: 'cancel' },
            {
              text: 'Update',
              role: 'confirm',
              handler: () => {
                fsm.updateFirmware(result, setUpdateStatus);
                updateCB?.(false);
              },
            },
          ];
        } else {
          if (failSilent) return;
          message = t('NO_FREAK_UPDATE_AVAILABLE');
        }
      } catch (error) {
        if (failSilent) return;
        message = t('FREAK_UPDATE_SEARCH_FAILED');
      }
      presentAlert({
        header: t('UPDATE'),
        message: message,
        buttons,
      });
    },
    [auth.userToken, t, presentAlert, fsm]
  );

  useEffect(() => {
    if (!updateStatus) return;

    const appRoot = document.querySelector('ion-app') || document.body;

    let container =
      document.querySelector('#spnx-overlay-div') ||
      document.createElement('div');
    container.id = 'spnx-overlay-div';

    const dismiss = () => {
      appRoot.removeChild(container);
      setUpdateStatus(undefined);
    };
    const element = UpdateOverlay({ updateStatus, dismiss });

    const root = createRoot(container);
    root.render(element);
    appRoot.appendChild(container);
  }, [updateStatus]);

  return { checkForUpdates };
};
